import React from "react";
import {
  MdOutlineCall,
  MdEmail,
  MdOutlineLocationOn,
  MdOutlinePerson,
  MdOutlineBadge,
  MdDescription,
  MdPictureAsPdf,
} from "react-icons/md";
import { AiOutlineFileWord, AiOutlineFileExcel } from "react-icons/ai";
import profileIcon from "../../../../Assets/DashboardAssets/profileIcon.png";
import { useTranslation } from "react-i18next";
import { Empty } from "antd";

const ViewStaff = ({ staff }) => {
  const { t } = useTranslation("admUserProfile");

  if (!staff) return null;

  const {
    profile,
    fullName,
    email,
    contactNumber,
    gender,
    address,
    role,
    employeeID,
    documents = [],
  } = staff;

  const city =
    address?.city ||
    address?.country ||
    address?.street ||
    "-";

  return (
    <div className="flex flex-col">
      {/* Profile Header */}
      <div className="flex flex-col justify-center items-center py-3">
        <img
          className="object-cover rounded-full w-[100px] h-[100px]"
          src={profile || profileIcon}
          alt={fullName || "Profile"}
        />
        <h3 className="text-lg font-semibold mt-2 capitalize">
          {fullName || "-"}
        </h3>
        <p className="text-pink-600 text-sm font-medium capitalize">
          {role || "Staff"}
        </p>
      </div>

      {/* Info Section */}
      <div className="flex flex-col gap-4 p-4 bg-gray-50 rounded-lg">
        <InfoItem icon={<MdOutlineBadge className="text-pink-600 text-2xl" />} label={t("Employee ID")} value={employeeID} />
        <InfoItem icon={<MdEmail className="text-pink-600 text-2xl" />} label={t("Email")} value={email} />
        <InfoItem icon={<MdOutlineCall className="text-pink-600 text-2xl" />} label={t("Phone")} value={contactNumber} />
        <InfoItem icon={<MdOutlinePerson className="text-pink-600 text-2xl" />} label={t("Gender")} value={gender} />
        <InfoItem icon={<MdOutlineLocationOn className="text-pink-600 text-2xl" />} label={t("Address")} value={city} />
      </div>

      {/* Documents Section */}
      <div className="mt-6">
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <MdOutlineBadge className="text-pink-600 text-2xl" />
          {t("Documents")}
        </h3>

        {documents.length > 0 ? (
          <div className="border rounded-md overflow-hidden">
            {/* Header Row */}
            <div className="hidden sm:grid grid-cols-4 bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-600">
              <span>{t("File")}</span>
              <span>{t("Type")}</span>
              <span>{t("Expiry")}</span>
              <span className="text-right">{t("Action")}</span>
            </div>

            {documents.map((doc, index) => (
              <DocumentRow key={doc._id || index} doc={doc} />
            ))}
          </div>
        ) : (
          <Empty description={t("No documents uploaded")} />
        )}
      </div>
    </div>
  );
};

const InfoItem = ({ icon, label, value }) => (
  <div className="flex items-center gap-3">
    {icon}
    <div className="flex flex-col">
      <span className="font-medium">{label}</span>
      <span className="text-gray-600 text-sm">{value || "-"}</span>
    </div>
  </div>
);

// Helpers
const getFileExtension = (nameOrUrl = "") => {
  const clean = nameOrUrl.split("?")[0];
  return clean.includes(".") ? clean.split(".").pop().toLowerCase() : "";
};

const getFileIcon = (ext) => {
  if (ext === "pdf") return <MdPictureAsPdf className="text-red-500 text-xl" />;
  if (["doc", "docx"].includes(ext)) return <AiOutlineFileWord className="text-blue-500 text-xl" />;
  if (["xls", "xlsx", "csv"].includes(ext)) return <AiOutlineFileExcel className="text-green-500 text-xl" />;
  return <MdDescription className="text-gray-500 text-xl" />;
};

const DocumentRow = ({ doc }) => {
  const ext = getFileExtension(doc?.name || doc?.url);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-4 items-center gap-2 px-4 py-3 border-t hover:bg-gray-50 transition">

      {/* File */}
      <div className="flex items-center gap-3 min-w-0">
        {getFileIcon(ext)}
        <span className="truncate font-medium">
          {doc?.name || "Document"}
        </span>
      </div>

      {/* Type */}
      <div className="text-sm text-gray-600 capitalize">
        {ext || "file"}
      </div>

      {/* Expiry */}
      <div className="text-sm text-gray-500">
        {doc?.expiryDate
          ? new Date(doc.expiryDate).toLocaleDateString()
          : "-"}
      </div>

      {/* Action */}
      <div className="flex gap-4 justify-end text-sm">
        <a
          href={doc.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-pink-600 hover:underline"
        >
          View
        </a>
      </div>
    </div>
  );
};

export default ViewStaff;
